import { CSSProperties, useEffect, useState } from "react";
import { ApiError, DownloadStats, ReleaseInfo, ReleasesBundle, api } from "../api/client";
import { config } from "../config";
import { useToast } from "../ui/toast";
import { IconCopy, IconRefresh } from "../ui/icons";
import ProductName from "../ProductName.jsx";

/**
 * Aplikacja / pobieranie — read-only view of the current build + history. Builds are
 * uploaded by the provider after agreed changes; this card only shows them and gives
 * the durable, on-domain download link to hand to customers.
 */
export function Releases() {
  const toast = useToast();
  const [channel, setChannel] = useState<"stable" | "beta">("stable");
  const [bundle, setBundle] = useState<ReleasesBundle | null>(null);
  const [stats, setStats] = useState<DownloadStats | null>(null);
  const [loading, setLoading] = useState(true);

  const downloadUrl = `${config.apiBaseUrl}/download?channel=${channel}`;

  const load = async () => {
    setLoading(true);
    try {
      setBundle(await api.ownerReleases(channel));
    } catch (e) {
      toast((e as ApiError).message);
      setBundle(null);
    } finally {
      setLoading(false);
    }
  };
  const loadStats = async () => {
    try {
      setStats(await api.downloadStats());
    } catch (e) {
      // Non-critical — a missing counter table just shows zeros.
      setStats({ stable: { total: 0, versions: [] }, beta: { total: 0, versions: [] } });
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]);
  useEffect(() => {
    loadStats();
  }, []);

  const chStats = stats?.[channel];

  const copy = () => {
    navigator.clipboard.writeText(downloadUrl).then(
      () => toast("Skopiowano link do pobrania"),
      () => toast("Nie udało się skopiować"),
    );
  };

  return (
    <>
      <div style={{ marginBottom: 14 }}><ProductName product="access-audit" size="md" /></div>
      <div className="section-hint" style={{ marginBottom: 14 }}>
        Buildy publikuje dostawca po uzgodnionych zmianach — tu je tylko przeglądasz. Link poniżej
        jest stały i na Twojej domenie; wysyłasz go klientom do pobrania aplikacji.
      </div>

      <div className="row" style={{ gap: 8, marginBottom: 14 }}>
        {(["stable", "beta"] as const).map((c) => (
          <button
            key={c}
            className={`btn sm ${channel === c ? "primary" : "ghost"}`}
            onClick={() => setChannel(c)}
          >
            {c === "stable" ? "Stabilny" : "Beta"}
          </button>
        ))}
      </div>

      {/* Download link */}
      <div className="card" style={{ padding: 20, marginBottom: 14, maxWidth: 860 }}>
        <div className="label">Link do pobrania (dla klientów)</div>
        <div className="row" style={{ gap: 8, alignItems: "center" }}>
          <input className="input mono" readOnly value={downloadUrl} style={{ flex: 1 }} />
          <button className="btn sm" onClick={copy}>
            <IconCopy /> Kopiuj
          </button>
          <a className="btn sm ghost" href={downloadUrl} target="_blank" rel="noreferrer">
            Pobierz
          </a>
        </div>
        <div className="faint" style={{ fontSize: 12, marginTop: 8 }}>
          Adres przekierowuje na bieżący instalator — bez tokenów w linku, zawsze aktualny.
        </div>
      </div>

      {/* Download counter */}
      <div className="card" style={{ padding: 20, marginBottom: 14, maxWidth: 860 }}>
        <div className="row" style={{ justifyContent: "space-between" }}>
          <div className="section-title">Pobrania</div>
          <button className="btn icon sm ghost" onClick={loadStats} title="Odśwież">
            <IconRefresh />
          </button>
        </div>
        <div className="row" style={{ gap: 24, marginTop: 8 }}>
          <div>
            <div className="faint" style={{ fontSize: 12 }}>Stabilny — łącznie</div>
            <div style={{ fontSize: 26, fontWeight: 700 }}>{stats?.stable.total ?? 0}</div>
          </div>
          <div>
            <div className="faint" style={{ fontSize: 12 }}>Beta — łącznie</div>
            <div style={{ fontSize: 26, fontWeight: 700 }}>{stats?.beta.total ?? 0}</div>
          </div>
        </div>
        <div className="faint" style={{ fontSize: 12, marginTop: 8 }}>
          Liczy wejścia na <code>/download</code> dla wybranego kanału (nie unikalne osoby).
        </div>
        <div style={{ marginTop: 12 }}>
          <div className="label">Kanał „{channel === "stable" ? "Stabilny" : "Beta"}" — wg wersji</div>
          {!chStats || chStats.versions.length === 0 ? (
            <div className="empty">Brak pobrań na tym kanale.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={TH}>Wersja</th>
                    <th style={TH}>Pobrania</th>
                    <th style={{ ...TH, whiteSpace: "nowrap" }}>Ostatnie</th>
                  </tr>
                </thead>
                <tbody>
                  {chStats.versions.map((v) => (
                    <tr key={v.version} style={{ borderTop: "1px solid var(--line)" }}>
                      <td style={TD}><b>v{v.version}</b></td>
                      <td style={TD}>{v.count}</td>
                      <td style={{ ...TD, whiteSpace: "nowrap" }} className="faint">
                        {v.lastAt ? new Date(v.lastAt).toLocaleString("pl-PL") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Current build */}
      <div className="card" style={{ padding: 20, marginBottom: 14, maxWidth: 860 }}>
        <div className="row" style={{ justifyContent: "space-between" }}>
          <div className="section-title">Bieżący build</div>
          <button className="btn icon sm ghost" onClick={load} title="Odśwież">
            <IconRefresh />
          </button>
        </div>
        {loading ? (
          <div className="empty">Wczytywanie…</div>
        ) : !bundle?.current ? (
          <div className="empty">Brak opublikowanego wydania na tym kanale.</div>
        ) : (
          <BuildDetails r={bundle.current} highlight />
        )}
      </div>

      {/* History */}
      <div className="card" style={{ padding: 20, maxWidth: 860 }}>
        <div className="section-title" style={{ marginBottom: 8 }}>Historia buildów</div>
        {loading ? (
          <div className="empty">Wczytywanie…</div>
        ) : !bundle?.history?.length ? (
          <div className="empty">Brak historii.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={TH}>Wersja</th>
                  <th style={{ ...TH, whiteSpace: "nowrap" }}>Data publikacji</th>
                  <th style={TH}>Co zmieniono</th>
                </tr>
              </thead>
              <tbody>
                {bundle.history.map((r) => (
                  <tr key={r.version} style={{ borderTop: "1px solid var(--line)", verticalAlign: "top" }}>
                    <td style={TD}><b>v{r.version}</b></td>
                    <td style={{ ...TD, whiteSpace: "nowrap" }} className="faint">
                      {r.publishedAt ? new Date(r.publishedAt).toLocaleString("pl-PL") : "—"}
                    </td>
                    <td style={TD}>
                      <div style={{ whiteSpace: "pre-wrap" }}>{r.notes?.trim() || "—"}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

const TH: CSSProperties = {
  textAlign: "left",
  padding: "8px 10px",
  fontSize: 12,
  fontWeight: 600,
  opacity: 0.7,
};
const TD: CSSProperties = { padding: "10px 10px", fontSize: 14 };

function BuildDetails({ r, highlight }: { r: ReleaseInfo; highlight?: boolean }) {
  return (
    <div>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ fontWeight: 700, fontSize: highlight ? 18 : 15 }}>
          v{r.version}
          {highlight && <span className="badge st-issued" style={{ marginLeft: 8 }}>aktualny</span>}
        </div>
        <span className="faint" style={{ fontSize: 12 }}>
          {r.publishedAt ? new Date(r.publishedAt).toLocaleString("pl-PL") : ""}
        </span>
      </div>
      {r.notes && <div style={{ marginTop: 6, whiteSpace: "pre-wrap" }}>{r.notes}</div>}
      {r.sha256 && (
        <div className="faint mono" style={{ fontSize: 11, marginTop: 6, wordBreak: "break-all" }}>
          SHA256: {r.sha256}
        </div>
      )}
    </div>
  );
}
